/**
 * WCC360 v2 — passions.
 *
 * Ported from v1, where passions were the one part of the flow learners
 * actually enjoyed. What is NOT ported is v1's scoring of them: there, a
 * matching passion family added up to 36 points on a 100-point scale, so
 * three taps could outrank twenty-two revealed-preference decisions. That
 * is a stated-interest signal beating a behavioural one, which is exactly
 * the failure v2 exists to fix.
 *
 * So passions enter the engine in two bounded ways:
 *
 *   1. ARCHETYPE NUDGE. Passion weights are folded into the archetype
 *      tally at a fraction of a Part 2 decision's weight, and the total
 *      contribution is capped (`MAX_ARCHETYPE_NUDGE`). Passions can break
 *      a near-tie between Creator and Builder. They cannot manufacture a
 *      primary archetype on their own.
 *
 *   2. FAMILY AFFINITY BONUS. A career whose family a learner named as a
 *      passion gets a small additive bonus (`PASSION_FAMILY_BONUS`) after
 *      the weighted score. It surfaces the work they said they cared
 *      about; it is far too small to promote a genuine misfit.
 *
 * Neither route can move a career into the "Best match" tier by itself,
 * and no eligibility gate reads passions at all — a passion must never
 * delete a career from the pool.
 *
 * CANON-PENDING: the v1 passion set carries an "Adventurer" archetype,
 * which is not one of the canonical eight. It is folded into Builder
 * (hands-on, physical, out-in-the-world work) rather than dropped, since
 * dropping it would silently zero the signal on ~20 passions. The mapping
 * belongs in the Command Center item bank; it is recorded here until then.
 */
import { PASSIONS, NONE_PASSION_ID, type Passion } from "@/data/passions";
import { ARCHETYPES, emptyVector, type ArchetypeVector } from "./archetypes";
import type { Archetype } from "@/lib/scoring";

/**
 * How many passions a learner may pick. Raising this is safe: every downstream
 * effect is capped in aggregate, not per pick — the archetype nudge is
 * normalised to `MAX_ARCHETYPE_NUDGE` across the whole set, and a career can
 * earn at most one family bonus (ceiling `PASSION_FAMILY_BONUS_STRONG`).
 */
export const MAX_PASSIONS = 7;

/** Total archetype points passions may contribute, across all picks. */
export const MAX_ARCHETYPE_NUDGE = 2;

/** Additive points a family match is worth on the final 0..100 score. */
export const PASSION_FAMILY_BONUS = 7;
/** A family named by two or more picks. Still under the smallest slot (11). */
export const PASSION_FAMILY_BONUS_STRONG = 10;

/** v1 archetype names → the canonical eight. */
const LEGACY_ARCHETYPE: Record<string, Archetype> = {
  Builder: "Builder",
  Thinker: "Thinker",
  Creator: "Creator",
  Organizer: "Organizer",
  Leader: "Leader",
  Caregiver: "Caregiver",
  Advocate: "Advocate",
  // Not canonical — see the CANON-PENDING note above.
  Adventurer: "Builder",
  // Defensive: older data used these names for Caregiver/Connector.
  Helper: "Caregiver",
  Connector: "Connector",
  Explorer: "Builder",
};

export function passionById(id: string): Passion | undefined {
  return PASSIONS.find((p) => p.id === id);
}

export function selectedPassions(ids: string[] | undefined): Passion[] {
  if (!ids || ids.includes(NONE_PASSION_ID)) return [];
  return ids.map(passionById).filter((p): p is Passion => Boolean(p));
}

/**
 * Archetype nudge from the learner's passions, normalised so the whole set
 * of picks can never contribute more than `MAX_ARCHETYPE_NUDGE` points in
 * total. Empty or "none" returns a zero vector, so a learner who skips
 * this screen is scored exactly as they were before it existed.
 */
export function passionArchetypeNudge(ids: string[] | undefined): ArchetypeVector {
  const picks = selectedPassions(ids);
  const raw = emptyVector();
  if (picks.length === 0) return raw;

  for (const p of picks) {
    for (const [name, w] of Object.entries(p.archetypeWeights)) {
      const canonical = LEGACY_ARCHETYPE[name];
      if (!canonical || !w) continue;
      raw[canonical] = (raw[canonical] ?? 0) + w;
    }
  }

  const total = ARCHETYPES.reduce((s, a) => s + (raw[a] ?? 0), 0);
  if (total <= 0) return raw;

  const scale = MAX_ARCHETYPE_NUDGE / total;
  const out = emptyVector();
  for (const a of ARCHETYPES) out[a] = (raw[a] ?? 0) * scale;
  return out;
}

/** Career families the learner's passions point at. Used for the bonus. */
export function passionFamilies(ids: string[] | undefined): string[] {
  const set = new Set<string>();
  for (const p of selectedPassions(ids)) for (const f of p.families) set.add(f);
  return [...set].sort();
}

/**
 * How many of the learner's picks point at each family. A family they named
 * twice is worth more than one they named once — but the ceiling is still
 * `PASSION_FAMILY_BONUS_STRONG`, so it can never outweigh a scoring slot.
 */
export function passionFamilyCounts(ids: string[] | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const p of selectedPassions(ids)) {
    for (const f of p.families) out.set(f, (out.get(f) ?? 0) + 1);
  }
  return out;
}

/** Human-readable reason line for the result card. */
export function passionReason(ids: string[] | undefined, family: string): string | null {
  const hit = selectedPassions(ids).find((p) => p.families.includes(family));
  return hit ? `it sits in ${family}, which you said you're drawn to (${hit.label})` : null;
}

/** Per-list caps inside the overall MAX_PASSIONS. Lists not named share the total. */
export const CATEGORY_LIMITS: Record<string, number> = {
  "The World You Want To Fix": 3,
  "How You Spend Your Own Time": 4,
};

/** Why a passion can't be added right now, or null when it can. */
export function pickBlock(selected: string[], id: string): "total" | "category" | null {
  if (selected.includes(id)) return null;
  if (selected.length >= MAX_PASSIONS) return "total";
  const cat = passionById(id)?.category;
  const limit = cat ? CATEGORY_LIMITS[cat] : undefined;
  if (limit !== undefined && selected.filter((s) => passionById(s)?.category === cat).length >= limit) return "category";
  return null;
}
